import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import Scroll from 'react-scroll';
import ReactSpinner from 'react16-spinjs';
import SVG from 'react-inlinesvg';
import settingsIcon from '@lifesize/ux-assets/vectors/svgIcons/settings.svg';
import starIcon from '@lifesize/ux-assets/vectors/svgIcons/star.svg';
import starOutlineIcon from '@lifesize/ux-assets/vectors/svgIcons/star-outline.svg';
import actions from 'actions/FullScreenModal';
import ButtonComp from 'components/Button/Button';
import ModalInput from 'components/ModalInput/ModalInput';
import Tooltip from 'components/Tooltip/Tooltip';
import ModalTextArea from 'components/ModalTextArea/ModalTextArea';
import ModeratorSearch from 'containers/ModeratorSearch/ModeratorSearchContainer';
import LecturerSearch from 'containers/LecturerSearch/LecturerSearchContainer';
import classes from './ContactForm.module.scss';
import {
  validatePasscode,
  validateRequiredPasscode,
} from 'utils/meeting';

const scroll = Scroll.animateScroll;

export const MEETING_FORM = 'meetingForm';

const messages = defineMessages({
  namePlaceholder: { id: 'components.contacts.meetingModal.namePlaceholder', defaultMessage: 'Name' },
  descriptionPlaceholder: { id: 'components.contacts.meetingModal.descriptionPlaceholder', defaultMessage: 'Add a description' },
  passcodePlaceholder: { id: 'components.contacts.meetingModal.passcodePlaceholder', defaultMessage: 'Set a numeric passcode' },
  passcodeRecommended: { id: 'components.contacts.meetingModal.passcodeRecommended', defaultMessage: '(recommended)' },
  passcodeRequired: { id: 'components.contacts.meetingModal.passcodeRequired', defaultMessage: '(required)' }
});

class MeetingForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAdvancedOptions: !props.showOneTimeMeeting
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.lecturerIsFocused && this.props.lecturerIsFocused && this.props.scrollableId) {
      scroll.scrollToBottom({
        containerId: this.props.scrollableId,
        smooth: true,
        duration: 200
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    if (event.code === 'Enter' || event.key === 'Enter') {
      event.preventDefault();
      if (!window.preventReload) {
        this.submit.click();
      }
    }
  };

  validateName = (value) => {
    if (!value || value.trim().length === 0) {
      return (
        <FormattedMessage
          id="components.contacts.meetingModal.nameRequiredError"
          defaultMessage="A meeting name is required."
        />);
    }

    return undefined;
  };

  validateModerator = () => {
    if (!this.props.moderator) {
      return (
        <FormattedMessage
          id="components.contacts.meetingModal.moderatorRequiredError"
          defaultMessage="A moderator is required."
        />);
    }

    return undefined;
  };

  render() {
    const {
      handleCancel,
      handleSubmit,
      intl,
      invalid,
      isCreate,
      lecturer,
      meeting,
      meetingTypeOneTime,
      meetingTypeVmr,
      moderator,
      pristine,
      reset,
      saveContact,
      savingContact,
      scrollableId,
      selectedMeetingType,
      showOneTimeMeeting,
      showPermanentMeeting,
      requireMeetingPasscode,
    } = this.props;

    const advancedOptions = (
      <div>
        <div data-testid='optionsForm' className={classes.optionsForm}>
          <Field
            component={ModalTextArea}
            label={intl.formatMessage(messages.descriptionPlaceholder)}
            maxlength={200}
            name="description"
            required={false}
            dataTestId="meeting-description"
          />
          <Field
            component={LecturerSearch}
            defaultValue={lecturer}
            name="lecturer"
            required={false}
            type="hidden"
          />
        </div>
      </div>
    );

    return (
      <div className={`${classes.createContactContainer} ${classes.forMeeting}`}>
        <form
          onSubmit={handleSubmit(saveContact)}
          className={`${classes.isMeeting}`}
        >
          { (showOneTimeMeeting ||  showPermanentMeeting ) &&
            <dl data-testid="divideForm" className={classes.dividedForm}>
              <dt className={classes.dividedTerm}>
                <FormattedMessage
                  id="components.contacts.meetingModal.meetingType"
                  defaultMessage="Meeting Type"
                />
              </dt>
              <dd className={classes.dividedDescription}>
                {showOneTimeMeeting &&
                  <label
                    className={classes.radioBtnContainer}
                    data-testid="radiobtn-one-time-meeting"
                  >
                    <FormattedMessage
                      id="components.contacts.meetingModal.oneTimeMeetingLabel"
                      defaultMessage="One-time Meeting"
                    />
                    <Field
                      component="input"
                      data-testid="meetingTypeOneTime"
                      id="meetingTypeOneTime"
                      name="meetingType"
                      type="radio"
                      value={meetingTypeOneTime}
                    />
                    <div className={classes.indicator} />
                  </label>
                }
                {showPermanentMeeting &&
                  <label
                    className={classes.radioBtnContainer}
                    data-testid="radiobtn-permanent"
                  >
                    <FormattedMessage
                      id="components.contacts.meetingModal.vmrMeetingLabel"
                      defaultMessage="Permanent Meeting"
                    />
                    <Field
                      component="input"
                      data-testid="meetingTypeVMR"
                      id="meetingTypeVMR"
                      name="meetingType"
                      type="radio"
                      value={meetingTypeVmr}
                    />
                    <div className={classes.indicator} />
                  </label>
                }
              </dd>
            </dl>}
          <div className={classes.createContactForm}>
            <Field
              autoComplete="new-password"
              autoFocus={!showOneTimeMeeting}
              component={ModalInput}
              label={intl.formatMessage(messages.namePlaceholder)}
              maxlength={200}
              name="name"
              required
              type="text"
              validate={this.validateName}
              dataTestId="meeting-name"
            />
            {((!meeting.id && selectedMeetingType !== meetingTypeOneTime) || meeting.isFavoritable) && (
              <label
                className={classes.checkboxContainer}
                data-testid="chkbox-favorite"
              >
                <Field
                  component="input"
                  data-testid="meeting-favorite-toggle"
                  id="createFavorite"
                  name="favorite"
                  type="checkbox"
                />
                <SVG src={starIcon} className={classes.checked} data-testid="starIcon" />
                <SVG src={starOutlineIcon} className={classes.unchecked} data-testid="starOutlineIcon" />
              </label>
            )}
            <Field
              component={ModeratorSearch}
              isCreate={isCreate}
              name="moderator"
              required
              type="hidden"
              validate={this.validateModerator}
              value={moderator}
              data-testid="moderator-dropdown"
            />
            <Field
              component="input"
              name="id"
              required={false}
              type="hidden"
            />
            <Field
              component="input"
              name="contactType"
              required={false}
              type="hidden"
            />
            <Field
              autoComplete="new-password"
              component={ModalInput}
              label={intl.formatMessage(messages.passcodePlaceholder)}
              maxlength={10}
              name="pin"
              required={requireMeetingPasscode}
              subText={intl.formatMessage(requireMeetingPasscode ? messages.passcodeRequired : messages.passcodeRecommended)}
              type="text"
              validate={requireMeetingPasscode ? validateRequiredPasscode : validatePasscode}
              dataTestId="meeting-pin"
            >
              <Tooltip
                location={'right'}
                shift={''}
                label={(
                  <FormattedMessage
                    id="components.meetingForm.passcode.tooltip"
                    defaultMessage="Meeting chat is not available when you use a passcode."
                  />
                )}
                style={{
                  textAlign: 'left',
                  whiteSpace: 'normal',
                  width: '170px',
                  fontStyle: 'normal',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '5px 13px',
                  fontSize: '12px'
                }}
              />
            </Field>
            <div
              className={classes.advancedOptionsContainer}
            >
              {showOneTimeMeeting &&
                <div className={classes.otherOptionsContainer}>
                  <button
                    className={classes.otherOptions}
                    onClick={(evt) => {
                      evt.preventDefault();
                      this.setState({ showAdvancedOptions: true });
                      scroll.scrollToBottom({
                        containerId: scrollableId,
                        smooth: true,
                        duration: 200
                      });
                    }}
                    data-testid="advanced-meeting-options"
                  >
                  <SVG src={settingsIcon} data-testid="settingsIcon" />
                    <FormattedMessage
                      id="components.meetingForm.advancedOptions.label"
                      defaultMessage="Other Options"
                    />
                  </button>
                </div>}
              {this.state.showAdvancedOptions && advancedOptions}
            </div>
            <input
              type="submit"
              id="submit-form"
              name="meetingModalSubmit"
              className={classes.hiddenSubmit}
            />
          </div>
          <button
            ref={(element) => {
              this.submit = element;
            }}
            type="submit"
            style={{ display: 'none' }}
          />
        </form>
        <div className={classes.buttonBar}>
          <ButtonComp
            kind="secondary"
            className={classes.contactButton}
            onClick={() => {
              reset();
              handleCancel();
            }}
            dataAttrs={{ 'data-testid': 'meet-form-cancel-btn' }}
          >
            <FormattedMessage
              id="components.contacts.meetingModal.cancelButton"
              defaultMessage="Cancel"
            />
          </ButtonComp>
          <ButtonComp
            className={classes.contactButton}
            kind="primary"
            disabled={pristine || invalid || savingContact}
            onClick={() => {
              document.getElementById('submit-form').click();
            }}
            dataAttrs={{ 'data-testid': 'meet-form-save-btn' }}
          >
            {savingContact ?
              <ReactSpinner
                config={{
                  lines: 13,
                  length: 5,
                  width: 3,
                  radius: 5,
                  color: '#fff',
                  speed: 0.7
                }}
              />
              :
              <FormattedMessage
                id="components.contacts.meetingModal.saveButton"
                defaultMessage="Save"
              />
            }
          </ButtonComp>
        </div>
      </div>
    );
  }
}

MeetingForm.propTypes = {
  handleCancel: PT.func,
  handleSubmit: PT.func,
  intl: PT.object,
  invalid: PT.bool,
  isCreate: PT.bool,
  lecturer: PT.object,
  lecturerIsFocused: PT.bool,
  meeting: PT.object,
  meetingTypeOneTime: PT.string,
  meetingTypeVmr: PT.string,
  moderator: PT.object,
  pristine: PT.bool,
  reset: PT.func,
  saveContact: PT.func,
  savingContact: PT.bool,
  scrollableId: PT.string,
  selectedMeetingType: PT.string,
  showOneTimeMeeting: PT.bool,
  showPermanentMeeting: PT.bool,
  requireMeetingPasscode: PT.bool
};

export default reduxForm({
  form: MEETING_FORM,
  fields: ['name', 'description', 'meetingType', 'pin', 'moderator', 'lecturer', 'contactType', 'id', 'favorite'],
  onSubmitSuccess: (values, dispatch, props) => {
    dispatch(actions.closeFullScreenModal());
  },
  getFormState: state => state.app.form
})(injectIntl(MeetingForm));
